<script setup lang="ts">
import { ref } from 'vue'

import { Button, Icon } from '@collector/shared-ui'
import { getDisplayedError } from '@collector/shared-utils'

import { ListItemProps } from '../../interfaces/ListItemProps'
import ListItemStatus from './ListItemStatus.vue'

const props = defineProps<ListItemProps>()

// state
const dataOpened = ref(false)
const errorOpened = ref(false)
const displayedText = ref('')

// methods
function toggleData(): void {
  dataOpened.value = !dataOpened.value
  errorOpened.value = false

  setDisplayText()
}

function toggleError(): void {
  errorOpened.value = !errorOpened.value
  dataOpened.value = false

  setDisplayText()
}

function setDisplayText(): void {
  if (dataOpened.value) {
    displayedText.value = String(props.displayedData)
  } else if (errorOpened.value) {
    displayedText.value = getDisplayedError(props.queueIncident.error)
  } else {
    displayedText.value = ''
  }
}
</script>

<template>
  <div data-testid="queue-incident">
    <div
      class="inline-flex items-center bg-linear-to-r px-1.5 text-sm"
      :class="{
        'to-current-team-home-5/40 from-transparent': teamSide === 'home',
        'to-current-team-away-5/40 from-transparent': teamSide === 'away',
        'to-error-9/40 from-transparent': incidentImportant,
        'bg-neutral-light-10': teamSide === null && !incidentImportant,
      }"
    >
      <div class="flex items-center">
        <div
          class="bg-neutral-light-10 text-neutral-3 mr-2 flex h-6 w-16 items-center justify-center rounded-sm border p-1"
        >
          {{ type }}
        </div>
      </div>
      <div class="my-1 mr-1 flex w-44 flex-col justify-center leading-normal">
        <div
          v-if="teamName"
          class="text-default/60 text-2xs font-bold"
        >
          {{ teamName }}
        </div>
        <div class="text-xs">
          {{ incidentTitle }}
          <span class="font-bold">{{ incidentName }}</span>
        </div>
      </div>
      <ListItemStatus :status />
      <div class="flex h-10 w-40 items-center justify-end gap-2">
        <Button
          v-if="queueIncident.error"
          class="text-neutral-light-10 bg-team-home-5 border-team-home-5 relative"
          size="sm"
          square
          data-testid="queue-incident-error-button"
          @click="toggleError"
        >
          <div class="flex flex-col">
            <Icon
              icon="warning"
              size="sm"
            />
            <Icon
              :icon="errorOpened ? 'angle-up' : 'angle-down'"
              size="xs"
            />
          </div>
        </Button>
        <Button
          class="text-neutral-light-10 bg-neutral-3 border-neutral-3 relative"
          size="sm"
          square
          data-testid="queue-incident-data-button"
          @click="toggleData"
        >
          <div class="flex flex-col">
            <Icon
              icon="circle-info"
              size="sm"
            />
            <Icon
              :icon="dataOpened ? 'angle-up' : 'angle-down'"
              size="xs"
            />
          </div>
        </Button>
        <Button
          class="bg-success-5 text-neutral-light-10 border-success-5 disabled:bg-success-5/40 disabled:border-transparent"
          :disabled
          icon="paper-plane"
          size="sm"
          square
          data-testid="queue-incident-resend-button"
          @click="eventIncidentsQueue.startProcessingQueueIncident(index)"
        />
        <Button
          class="bg-error-5 text-neutral-light-10 border-error-5 disabled:bg-error-5/40 disabled:border-transparent"
          :disabled
          icon="times"
          size="sm"
          square
          data-testid="queue-incident-remove-button"
          @click="eventIncidentsQueue.removeQueueIncident(index)"
        />
      </div>
    </div>
    <div
      v-if="displayedText"
      class="bg-neutral-dark-10/10 mt-1 p-2 text-xs break-words"
      :class="{
        'whitespace-pre': dataOpened,
      }"
    >
      {{ displayedText }}
    </div>
  </div>
</template>
